"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";
import { Spinner } from "./spinner";

export interface LoadingStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Loading message */
  message?: string;
  /** Spinner size */
  size?: "sm" | "md" | "lg" | "xl";
  /** Layout orientation */
  orientation?: "horizontal" | "vertical";
  /** Whether to center within parent */
  centered?: boolean;
  /** Full screen overlay mode */
  fullScreen?: boolean;
  /** Overlay background (for fullScreen mode) */
  overlay?: boolean;
}

/**
 * LoadingState displays a loading indicator with optional message.
 * Use it to indicate that content is being loaded.
 */
export function LoadingState({
  className,
  message,
  size = "md",
  orientation = "vertical",
  centered = true,
  fullScreen = false,
  overlay = false,
  ...props
}: LoadingStateProps) {
  const content = (
    <div
      className={cn(
        styleDrivers.loadingState.root,
        orientation === "vertical" && "flex-col text-center",
        centered && "justify-center",
        className
      )}
      {...props}
    >
      <Spinner size={size} />
      {message && (
        <div className={styleDrivers.loadingState.message}>{message}</div>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className={cn(
          styleDrivers.loadingState.fullScreen,
          !overlay && "bg-transparent backdrop-blur-none"
        )}
      >
        {content}
      </div>
    );
  }

  if (centered) {
    return (
      <div className={styleDrivers.loadingState.centeredContainer}>
        {content}
      </div>
    );
  }

  return content;
}

LoadingState.displayName = "LoadingState";

/**
 * LoadingOverlay displays a loading indicator over existing content.
 * Use it when updating content that should remain visible.
 */
export interface LoadingOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether loading is active */
  isLoading?: boolean;
  /** Loading message */
  message?: string;
  /** Blur the content behind */
  blur?: boolean;
  /** Children to render underneath */
  children: React.ReactNode;
}

export function LoadingOverlay({
  className,
  isLoading = false,
  message,
  blur = true,
  children,
  ...props
}: LoadingOverlayProps) {
  return (
    <div className={cn("relative", className)} {...props}>
      {children}
      {isLoading && (
        <div
          className={cn(
            styleDrivers.loadingState.overlay,
            !blur && "backdrop-blur-none"
          )}
        >
          <LoadingState message={message} />
        </div>
      )}
    </div>
  );
}

LoadingOverlay.displayName = "LoadingOverlay";

/**
 * InlineLoading displays a small inline loading indicator.
 * Use it for button loading states or inline status.
 */
export interface InlineLoadingProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Loading message */
  message?: string;
}

export function InlineLoading({
  className,
  message,
  ...props
}: InlineLoadingProps) {
  return (
    <div
      className={cn("inline-flex items-center gap-2", className)}
      {...props}
    >
      <Spinner size="sm" />
      {message && <div className="text-sm">{message}</div>}
    </div>
  );
}

InlineLoading.displayName = "InlineLoading";

export default LoadingState;
